import { useEffect, useRef } from 'react';

/**
 * Модалка-плейсхолдер для нереализованных блоков (`available=false` в HOME_BLOCKS).
 * В Phase 1 это блоки 1, 5-7, 8-11 (10 из 13 блоков). По мере реализации каждой
 * фазы соответствующий блок переходит в `available=true` и эта модалка для него
 * перестаёт открываться.
 *
 * UI зеркалирует PaywallModal стилистически (overlay 50%, центральная карточка
 * bg-canvas radius 20), но имеет одну кнопку «Понятно» (закрытие) — поэтому
 * вынесено в отдельный компонент. Если в будущем появится третий use case
 * (например, generic info-модалка) — рефакторим в shared `Modal` примитив
 * (см. правило «3rd consumer trigger» в BACKLOG B-049).
 *
 * a11y: role=dialog, aria-modal, aria-labelledby. Initial focus на «Понятно».
 * Закрытие: overlay click, Escape, кнопка «Понятно».
 */
export interface UnavailableModalProps {
  onClose: () => void;
}

export function UnavailableModal({ onClose }: UnavailableModalProps) {
  const okButtonRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') onClose();
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  useEffect(() => {
    okButtonRef.current?.focus();
  }, []);

  return (
    <div
      onClick={onClose}
      style={{ position: 'fixed', inset: 0, zIndex: 100, padding: 20 }}
      className="flex items-center justify-center bg-black/50"
      data-testid="unavailable-overlay"
    >
      <div
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="unavailable-modal-title"
        className="w-full bg-canvas text-center"
        style={{
          maxWidth: 360,
          borderRadius: 20,
          padding: 24,
          boxShadow: '0 16px 64px rgba(0,0,0,.4)',
        }}
      >
        <h2
          id="unavailable-modal-title"
          className="font-bold text-ink leading-snug"
          style={{ fontSize: 22, margin: '0 0 24px' }}
        >
          Функция в разработке. Доступно в следующих обновлениях.
        </h2>
        <button
          ref={okButtonRef}
          type="button"
          onClick={onClose}
          aria-label="Понятно"
          className={
            'w-full font-bold bg-cta text-ink border-b-2 border-cta-shadow ' +
            'min-h-tap-chip-lg rounded-2xl ' +
            'focus-visible:ring-2 focus-visible:ring-cta focus-visible:ring-offset-2 ' +
            'focus-visible:outline-none active:opacity-90 transition-opacity'
          }
          style={{ fontSize: 19 }}
        >
          Понятно
        </button>
      </div>
    </div>
  );
}
